import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { CollectionName, CollectionMap } from "@lockcal/shared";

/**
 * Local cache.
 *
 * Every synced record is stored with a `_dirty` flag. A record is dirty when it
 * has local changes that the server has not acknowledged yet.
 */
export interface LocalRecord {
  id: string;
  _dirty: 0 | 1;
  _deleted: 0 | 1;
  updatedAt: number;
  deletedAt: number | null;
  [key: string]: unknown;
}

interface LockCalDB extends DBSchema {
  calendars: { key: string; value: LocalRecord; indexes: { byUpdatedAt: number } };
  events: { key: string; value: LocalRecord; indexes: { byUpdatedAt: number } };
  reminderLists: { key: string; value: LocalRecord; indexes: { byUpdatedAt: number } };
  reminders: { key: string; value: LocalRecord; indexes: { byUpdatedAt: number } };
  habits: { key: string; value: LocalRecord; indexes: { byUpdatedAt: number } };
  habitCheckIns: { key: string; value: LocalRecord; indexes: { byUpdatedAt: number } };
  stickyNotes: { key: string; value: LocalRecord; indexes: { byUpdatedAt: number } };
  settings: { key: string; value: LocalRecord; indexes: { byUpdatedAt: number } };
  meta: { key: string; value: unknown };
}

export const STORE_NAMES: CollectionName[] = [
  "calendars",
  "events",
  "reminderLists",
  "reminders",
  "habits",
  "habitCheckIns",
  "stickyNotes",
  "settings",
];

let dbPromise: Promise<IDBPDatabase<LockCalDB>> | null = null;

export function db(): Promise<IDBPDatabase<LockCalDB>> {
  if (!dbPromise) {
    dbPromise = openDB<LockCalDB>("lockcal", 1, {
      upgrade(database) {
        for (const name of STORE_NAMES) {
          const store = database.createObjectStore(name, { keyPath: "id" });
          store.createIndex("byUpdatedAt", "updatedAt");
        }
        database.createObjectStore("meta");
      },
    });
  }
  return dbPromise;
}

/* ------------------------------------------------------------------ */
/* meta                                                               */
/* ------------------------------------------------------------------ */

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const database = await db();
  const value = await database.get("meta", key);
  return (value as T | undefined) ?? fallback;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  const database = await db();
  await database.put("meta", value, key);
}

/** Stable id for this browser/device, used for conflict tie-breaking. */
export async function deviceId(): Promise<string> {
  let id = await getMeta<string | undefined>("deviceId", undefined);
  if (!id) {
    id = `web-${crypto.randomUUID()}`;
    await setMeta("deviceId", id);
  }
  return id;
}

/* ------------------------------------------------------------------ */
/* records                                                            */
/* ------------------------------------------------------------------ */

export async function putRecord(
  collection: CollectionName,
  record: LocalRecord,
): Promise<void> {
  const database = await db();
  await database.put(collection, record);
}

export async function putRecords(
  collection: CollectionName,
  records: LocalRecord[],
): Promise<void> {
  if (records.length === 0) return;
  const database = await db();
  const tx = database.transaction(collection, "readwrite");
  await Promise.all([...records.map((r) => tx.store.put(r)), tx.done]);
}

export async function getRecord(
  collection: CollectionName,
  id: string,
): Promise<LocalRecord | undefined> {
  const database = await db();
  return database.get(collection, id);
}

export async function allRecords(
  collection: CollectionName,
): Promise<LocalRecord[]> {
  const database = await db();
  return database.getAll(collection);
}

export async function deleteRecord(
  collection: CollectionName,
  id: string,
): Promise<void> {
  const database = await db();
  await database.delete(collection, id);
}

export async function dirtyRecords(): Promise<
  { collection: CollectionName; record: LocalRecord }[]
> {
  const database = await db();
  const out: { collection: CollectionName; record: LocalRecord }[] = [];
  for (const name of STORE_NAMES) {
    const all = await database.getAll(name);
    for (const record of all) {
      if (record._dirty === 1) out.push({ collection: name, record });
    }
  }
  return out;
}

export async function clearAll(): Promise<void> {
  const database = await db();
  for (const name of STORE_NAMES) {
    await database.clear(name);
  }
  await database.clear("meta");
}

/* ------------------------------------------------------------------ */
/* typed helpers                                                      */
/* ------------------------------------------------------------------ */

export function asType<T>(record: LocalRecord): T {
  return record as unknown as T;
}

export type { CollectionMap };
